import { notifyAnalytics } from '@/api/analytics/notifyAnalytics'
import type { ValidEndpoints } from '@/api/clients/backend-client'
import type {
  DirectusCardRecordFiles,
  DirectusModelImageLink,
  DirectusUpdateVehicleImagesRes,
  DirectusUserSelectedPlug,
  DirectusVehicle
} from '@/types/directus-types'
import type { AdvancedConfig } from '@/types/directus-types/directus_vehicle'
import { fakeNoData } from './bad-placeholder-helpers'
import { process_number_or_null, type CreateConverter, type CrudConverter } from './converter-helpers'
import { createItem, fetchCollection, updateItem } from './helper-functions'

const vehicles_collection: ValidEndpoints = 'vehicles'
const vehicle_images_collection: ValidEndpoints = 'vehicle_images'

type Vehicles_expected_API_DTO = {
  id: string
  cdb_model_id?: string | null
  rego?: string | null
  name?: string | null
  fuel_type: string
  connectors_override?: Record<string, unknown> | null
  model_profile_override?: Record<string, unknown> | null
  soh?: number | null
}

type Vehicle_images_API_DTO = {
  id: string
  vehicle_id: string
  image_url: string
  uploaded_by_user_id?: string
  uploaded_at?: string
}

const parse_model_profile_override = (
  model_profile_override?: Record<string, unknown> | null,
): AdvancedConfig | null => {
  if (!model_profile_override) return null
  if (Object.keys(model_profile_override))
    return {
      AccelerationAdjustment: undefined, // TODO: should be removed from vehicle profiles
      AdditionalWeight:
        process_number_or_null(model_profile_override.additional_weight) ?? undefined,
      BatterySize: process_number_or_null(model_profile_override.battery_size) ?? undefined,
      DragCoefficient: process_number_or_null(model_profile_override.drag_coefficient) ?? undefined,
      EnergyUsedWhenMoving:
        process_number_or_null(model_profile_override.energy_used_when_moving) ?? undefined,
      EnergyUsedWhenStopped:
        process_number_or_null(model_profile_override.energy_used_when_stopped) ?? undefined,
      Mass: process_number_or_null(model_profile_override.mass) ?? undefined,
      MaxElectricPowerAc:
        process_number_or_null(model_profile_override.max_electric_power_AC) ?? undefined,
      MaxElectricPowerDc:
        process_number_or_null(model_profile_override.max_electric_power_DC) ?? undefined,
      PowerChainEfficiency:
        process_number_or_null(model_profile_override.power_chain_efficiency) ?? undefined,
      RegenerativeBreakingEfficiency:
        process_number_or_null(model_profile_override.regenerative_breaking_efficiency) ??
        undefined,
      RollingResistanceCoefficient:
        process_number_or_null(model_profile_override.rolling_resistance_coefficient) ?? undefined,
      SpeedAdjustment: undefined, // TODO: should be removed from vehicle profiles
      WeatherFactor: undefined, // TODO: weather factor is still not implemented
    }
  return null
}

const process_model_profile_override = (
  AdvancedConfig?: AdvancedConfig | null | undefined,
): Record<string, unknown> | null => {
  if (!AdvancedConfig) return null
  if (!Object.keys(AdvancedConfig).length) return null
  const capture_obj: Record<string, unknown> = {}
  if (AdvancedConfig.AdditionalWeight)
    capture_obj.additional_weight = AdvancedConfig.AdditionalWeight
  if (AdvancedConfig.BatterySize) capture_obj.battery_size = AdvancedConfig.BatterySize
  if (AdvancedConfig.DragCoefficient) capture_obj.drag_coefficient = AdvancedConfig.DragCoefficient
  if (AdvancedConfig.EnergyUsedWhenMoving)
    capture_obj.energy_used_when_moving = AdvancedConfig.EnergyUsedWhenMoving
  if (AdvancedConfig.EnergyUsedWhenStopped)
    capture_obj.energy_used_when_stopped = AdvancedConfig.EnergyUsedWhenStopped
  if (AdvancedConfig.Mass) capture_obj.mass = AdvancedConfig.Mass
  if (AdvancedConfig.MaxElectricPowerAc)
    capture_obj.max_electric_power_AC = AdvancedConfig.MaxElectricPowerAc
  if (AdvancedConfig.MaxElectricPowerDc)
    capture_obj.max_electric_power_DC = AdvancedConfig.MaxElectricPowerDc
  if (AdvancedConfig.PowerChainEfficiency)
    capture_obj.power_chain_efficiency = AdvancedConfig.PowerChainEfficiency
  if (AdvancedConfig.RegenerativeBreakingEfficiency)
    capture_obj.regenerative_breaking_efficiency = AdvancedConfig.RegenerativeBreakingEfficiency
  if (AdvancedConfig.RollingResistanceCoefficient)
    capture_obj.rolling_resistance_coefficient = AdvancedConfig.RollingResistanceCoefficient
  if (Object.keys(capture_obj).length) return capture_obj
  return null
}

const parse_connectors_override = (
  connectors_override?: Record<string, unknown> | null,
): string[] | null => {
  if (!connectors_override) return null
  return Object.values(connectors_override)
    .filter((value): value is Record<string, unknown> => {
      return typeof value === 'object' && value !== null && !Array.isArray(value)
    })
    .map((obj) => JSON.stringify(obj))
}

const process_connectors_override = (
  UserSelectedPlugs?: string[] | null,
): Record<string, unknown> | null => {
  const plugs: DirectusUserSelectedPlug[] = []
  UserSelectedPlugs?.forEach((plug) => {
    try {
      const parsedPlug: DirectusUserSelectedPlug = JSON.parse(plug)
      plugs.push(parsedPlug)
    } catch (error) {
      // TODO: do a thing maybe this is place holder code anyway
    }
  })
  if (!plugs.length) return null
  const return_obj: Record<string, unknown> = {}
  plugs.forEach((plug) => {
    return_obj[plug.standard] = plug
  })
  return return_obj
}

const vehiclesConverter: CrudConverter<
  Vehicles_expected_API_DTO,
  DirectusVehicle,
  Partial<DirectusVehicle>,
  Partial<DirectusVehicle>
> = {
  apiToLegacy(api) {
    return {
      id: api.id,
      CDB_Model_ID: api.cdb_model_id ?? null,
      AdvancedConfig: parse_model_profile_override(api.model_profile_override),
      created_by: null,
      Driver: null,
      from_slurper: false,
      fuel_type: api.fuel_type,
      Images: null,
      name: api.name ?? null,
      rego: api.rego ?? null,
      soh: api.soh ?? null,
      UserSelectedPlugs: parse_connectors_override(api.connectors_override),
      vin: null,
    }
  },
  legacyToApi(legacy) {
    return {
      cdb_model_id: legacy.CDB_Model_ID,
      fuel_type: legacy.fuel_type?.toUpperCase() ?? 'OTHER',
      name: legacy.name,
      rego: legacy.rego,
      id: legacy.id.toString(),
      connectors_override: process_connectors_override(legacy.UserSelectedPlugs),
      soh: legacy.soh,
      model_profile_override: process_model_profile_override(legacy.AdvancedConfig),
    }
  },
  createToApi(create) {
    return {
      cdb_model_id: create.CDB_Model_ID,
      fuel_type: create.fuel_type?.toUpperCase() ?? 'OTHER',
      name: create.name,
      rego: create.rego,
      connectors_override: process_connectors_override(create.UserSelectedPlugs),
      soh: create.soh,
      model_profile_override: process_model_profile_override(create.AdvancedConfig),
    }
  },
  updateToApi(updated) {
    const collection_obj: Partial<Vehicles_expected_API_DTO> = {}
    if (updated.AdvancedConfig)
      collection_obj.model_profile_override = process_model_profile_override(updated.AdvancedConfig)
    if (updated.CDB_Model_ID) collection_obj.cdb_model_id = updated.CDB_Model_ID
    if (updated.UserSelectedPlugs)
      collection_obj.connectors_override = process_connectors_override(updated.UserSelectedPlugs)
    if (updated.fuel_type) collection_obj.fuel_type = updated.fuel_type.toUpperCase()
    if (updated.name) collection_obj.name = updated.name
    if (updated.rego) collection_obj.rego = updated.rego
    if (updated.soh) collection_obj.soh = updated.soh
    return collection_obj
  },
}


const vehicleImagesConverter: CreateConverter<Vehicle_images_API_DTO, DirectusCardRecordFiles, Partial<DirectusCardRecordFiles>> = {
  apiToLegacy(api) {
    return {
      Car_Record_id: api.vehicle_id,
      image_url: api.image_url,
      id: api.id,
      directus_files_id: api.image_url
    }
  },
  createToApi(create) {
    return {
      vehicle_id: create.Car_Record_id?.toString(),
      image_url: create.directus_files_id
    }
  },
  legacyToApi(legacy) {
    return {
      id: legacy.id.toString(),
      image_url: legacy.image_url,
      vehicle_id: legacy.Car_Record_id.toString(),
    }
  },
}

/**
 * Uploads an image to the "Car_Record_files" collection and links it to the passed vehicle directus id.
 *
 * @param directus_vehicle_id the id of the vehicle to link the uploaded image to.
 * @param imageToAdd the image to upload.
 * @returns an object containing the success status and the UUID of the uploaded image.
 */
export async function updateVehicleImages(
  directus_vehicle_id: number | string,
  imageToAdd: File,
): Promise<DirectusUpdateVehicleImagesRes> {
  const collection = 'Car_Record_files'
  try {
    const uploadRes = await Promise.resolve({  success: false as boolean,
      UUID: "fake id"})
    if (uploadRes.success && uploadRes.UUID) {

      await createItem<Vehicle_images_API_DTO, DirectusCardRecordFiles>(
        vehicle_images_collection,
        vehicleImagesConverter.createToApi({
          Car_Record_id: directus_vehicle_id,
          image_url: uploadRes.UUID,
          directus_files_id: uploadRes.UUID
        }),
        vehicleImagesConverter.apiToLegacy
      )
      
      return {
        success: true,
        imageUUID: uploadRes.UUID,
      }
    } else {
      return { success: false }
    }
  } catch (error) {
    notifyAnalytics({
      type: 'log_error',
      data: error,
    })
    return { success: false }
  }
}

/**
 * API Call to update a vehicle records model in directus.
 *
 * @param vehicleID the ID of the vehicle record to update.
 * @param newSOH the new state of health value to update the vehicle record with.
 * @returns the updated `Vehicle` collection record if successful, undefined if not.
 */
export async function updateVehicleSOH(
  vehicleID: number | string,
  newSOH: number,
): Promise<DirectusVehicle | undefined> {
  return await updateVehicle(vehicleID, { soh: newSOH })
}

/**
 * API Call to update a vehicle records model in directus.
 *
 * @param vehicleID the ID of the vehicle record to update.
 * @param modelID the ID of the model to update the vehicle record with.
 * @returns the updated `Vehicle` collection record if successful, undefined if not.
 */
export async function updateVehicleModel(
  vehicleID: number | string,
  modelID?: string,
): Promise<DirectusVehicle | undefined> {
  return await updateVehicle(vehicleID, { CDB_Model_ID: modelID })
}

/**
 * API Call to update a `Vehicle` collection records `UserSelectedPlugs` array in directus.
 *
 * @param vehicleID the ID of the vehicle record to update.
 * @param plugs the array of `DirectusUserSelectedPlug` objects to update with.
 * @returns the updated `Vehicle` collection record if successful, undefined if not.
 */
export async function updateVehiclePlugs(
  vehicleID: number | string,
  plugs: DirectusUserSelectedPlug[] | null,
): Promise<DirectusVehicle | undefined> {
  let convertedPlugs: string[] | null = null
  if (plugs) {
    // convert the plugs to an array of strings by stringifying each of the objects.
    convertedPlugs = plugs.map((plug) => JSON.stringify(plug))
  }
  return await updateVehicle(vehicleID, {
    UserSelectedPlugs: convertedPlugs,
  })
}

/**
 * API call to fetch fleet vehicles.
 *
 * @returns a list of directus `VehicleData` collection objects.
 */
export async function fetchFleetVehicles(): Promise<DirectusVehicle[] | undefined> {
  return await fetchCollection<Vehicles_expected_API_DTO, DirectusVehicle >(
    vehicles_collection,
    undefined,
    vehiclesConverter.apiToLegacy
  )
}

/**
 * API call to fetch image asset links for all supported EV models from directus.
 *
 * @returns a list of `DirectusModelImageLink` link objects.
 */
export async function fetchEVModelImageLinks(): Promise<DirectusModelImageLink[] | undefined> {
  const collection = 'EV_Model_Assets'
  const query = { limit: -1 }

  const res = await fakeNoData()
  return res
}

/**
 * API call to create a new directus `Vehicle` collection record.
 *
 * @param vehicleData the whole `DirectusVehicleCreationData` object.
 * @returns a new directus `Vehicle` collection record if successful (`VehicleData` object).
 */
export async function createNewVehicle(
  vehicleData: Partial<DirectusVehicle>,
): Promise<DirectusVehicle | undefined> {
  const res = await createItem<Vehicles_expected_API_DTO, DirectusVehicle>(
    vehicles_collection,
    vehiclesConverter.createToApi(vehicleData),
    vehiclesConverter.apiToLegacy,
  )
  return res as unknown as DirectusVehicle
}

/**
 * Updates an existing directus `Vehicle` collection record.
 *
 * @param directusId - The id of the directus `Vehicle` collection record to update.
 * @param dataToUpdate - An object containing the vehicle data to be updated.
 * @returns The updated `DirectusVehicle` object if successful.
 */

export async function updateVehicle(
  directusId: number | string,
  dataToUpdate: Partial<DirectusVehicle>,
): Promise<DirectusVehicle> {
  const res = await updateItem<DirectusVehicle>(
    vehicles_collection,
    directusId.toString(),
    vehiclesConverter.updateToApi(dataToUpdate),
    vehiclesConverter.apiToLegacy,
  )
  return res as unknown as DirectusVehicle
}
